import { useBeneficiosDaComposicao } from '@/hooks/useBeneficiosDaComposicao';
import { megabonusDaProposta } from '../../../../backend/src/services/whatsapp/gerar-mensagem-proposta';
/**
 * Componente de Comparativo Visual de Proposta
 * Mostra lado a lado "O que TENHO" vs "O que FICO"
 * Otimizado para impressão e envio ao cliente
 */
import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import { toPng } from 'html-to-image';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { 
  Phone, 
  Wifi, 
  TrendingDown, 
  ArrowRight, 
  Copy, 
  Download,
  Check,
  RefreshCw,
  ArrowRightLeft,
  Sparkles,
  Tv,
  Globe,
} from 'lucide-react';

import { type ComposicaoProposta, formatarMoedaPlano, type TipoLinha, ADICIONAIS_TV, ADICIONAIS_FIBRA, getAdicionalMovelById, gerarMensagemPropostaCompleta, listarAparelhos } from '@/constants/planosCNPJ';
import { formatarTelefone, formatarCNPJ } from '@/utils/formatters';
import { toast } from 'sonner';

interface PlanoAtual {
  linhas: string[];
  linhasDetalhadas?: Array<{ numero: string; gb: number }>; // GB por linha da fatura
  qtdLinhas?: number; // Quantidade editada de linhas (prioridade sobre linhas.length)
  valor: number;
  tipo: 'COMBO' | 'MÓVEL' | 'RESIDENCIAL';
  dataFatura?: string;
  gb?: number;
  fibra?: string;
  tv?: string; // Campo para TV do plano atual
  origem?: 'PF' | 'CNPJ' | 'VIVO'; // Origem do plano: PF para TT, CNPJ para Renovação, VIVO para Portabilidade
  // Dados fixos (PORTABILIDADE_VIVO)
  linhasFixas?: number;
  valorFixo?: number;
  fibraVelocidade?: string;
  telefonesFixos?: string[];
}

interface ComparativoPropostaVisualProps {
  planoAtual: PlanoAtual;
  composicaoNova?: ComposicaoProposta | null; // Opcional - pode estar vazio ainda
  composicaoNova2?: ComposicaoProposta | null; // Plano Novo 2 — Renovação com Incremento
  nomeCliente?: string;
  cnpj?: string;
  consultorNome?: string;
  ocultarPlanoAtual?: boolean; // Novo: ocultar coluna do plano atual
  ocultarPlanoFixo?: boolean; // Novo: ocultar dados fixos
  consultorFotoUrl?: string; // Foto do consultor logado
}

// 🆕 Handle exposto via ref pra capturar o card como Blob/dataURL sem disparar download.
// Usado pelo modal de envio (Proposta Imagem).
export interface ComparativoPropostaVisualHandle {
  gerarImagemBlob: () => Promise<Blob>;
}

const TIPO_ICONE: Record<TipoLinha, React.ReactNode> = {
  TT: <RefreshCw className="w-3 h-3" />,
  TT_PRE: <RefreshCw className="w-3 h-3" />,
  PORTABILIDADE: <ArrowRightLeft className="w-3 h-3" />,
  NOVA: <Sparkles className="w-3 h-3" />,
  FIBRA: <Wifi className="w-3 h-3" />,
  TV: <Tv className="w-3 h-3" />,
  RENOVACAO: <RefreshCw className="w-3 h-3" />,
  LINHA_NOVA: <Phone className="w-3 h-3" />,
  SERVICO: <Sparkles className="w-3 h-3" />,
};

const TIPO_LABEL: Record<TipoLinha, string> = {
  TT: 'TT',
  TT_PRE: 'TT PRE',
  PORTABILIDADE: 'PORT',
  NOVA: 'BLOCO',
  FIBRA: 'FIBRA',
  TV: 'TV',
  RENOVACAO: 'REN',
  LINHA_NOVA: 'NOVA',
  SERVICO: 'SERVIÇO',
};

export const ComparativoPropostaVisual = forwardRef<ComparativoPropostaVisualHandle, ComparativoPropostaVisualProps>(function ComparativoPropostaVisual({
  planoAtual,
  composicaoNova,
  composicaoNova2,
  nomeCliente,
  cnpj,
  consultorNome,
  ocultarPlanoAtual = false,
  ocultarPlanoFixo = false,
  consultorFotoUrl,
}: ComparativoPropostaVisualProps, ref) {
  const cardRef = useRef<HTMLDivElement>(null);

  // 🆕 Helper compartilhado: prepara o card num wrapper branco e retorna dataUrl.
  // Reutilizado tanto pelo download (gerarImagem) quanto pelo envio WhatsApp (gerarImagemBlob).
  const renderizarCardComoPng = async (): Promise<string> => {
    if (!cardRef.current) throw new Error("Card nao montado");
    // Aguardar imagens carregarem
    const imagens = Array.from(cardRef.current.querySelectorAll('img')) as HTMLImageElement[];
    await Promise.all(imagens.map((img) => img.complete ? Promise.resolve() : new Promise<void>((r) => { img.onload = () => r(); img.onerror = () => r(); })));
    if (document.fonts?.ready) await document.fonts.ready;
    await new Promise(resolve => setTimeout(resolve, 200));
    const originalWidth = cardRef.current.offsetWidth;
    const wrapper = document.createElement('div');
    wrapper.style.cssText = `display: inline-block; padding: 20px; background-color: #ffffff;`;
    const clone = cardRef.current.cloneNode(true) as HTMLElement;
    clone.style.width = `${originalWidth}px`;
    clone.style.border = '2px solid hsl(var(--primary))';
    clone.style.borderRadius = '12px';
    clone.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
    clone.style.overflow = 'hidden';
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);
    try {
      const dataUrl = await toPng(wrapper, {
        quality: 1, pixelRatio: 2, backgroundColor: '#ffffff', skipFonts: false, includeQueryParams: true,
      });
      return dataUrl;
    } finally {
      document.body.removeChild(wrapper);
    }
  };

  // 🆕 Exposto via ref: gera Blob PNG do card sem disparar download.
  useImperativeHandle(ref, () => ({
    gerarImagemBlob: async (): Promise<Blob> => {
      const dataUrl = await renderizarCardComoPng();
      const resp = await fetch(dataUrl);
      return await resp.blob();
    },
  }), []);
  
  // Se não tiver composição ainda, mostrar placeholder
  const temComposicao = composicaoNova && composicaoNova.linhas && composicaoNova.linhas.length > 0;
  const temComposicao2 = composicaoNova2 && composicaoNova2.linhas && composicaoNova2.linhas.length > 0;

  // Plano Novo 2 — linhas separadas
  const linhasMoveis2 = temComposicao2
    ? composicaoNova2!.linhas.filter(l => l.tipo !== 'FIBRA' && l.tipo !== 'TV')
    : [];
  const linhasNovas2 = linhasMoveis2.filter(l => l.tipo === 'LINHA_NOVA');
  const linhasRen2 = linhasMoveis2.filter(l => l.tipo !== 'LINHA_NOVA');
  
  // Valores do plano novo (ou zeros se não tiver composição)
  const totalValorNovo = temComposicao ? composicaoNova.totalValor : 0;
  const economia = planoAtual.valor - totalValorNovo;
  const economiaPercent = planoAtual.valor > 0 
    ? ((economia / planoAtual.valor) * 100).toFixed(0) 
    : '0';
  const origemLabel = planoAtual.origem || 'PF'; // Default PF para retrocompatibilidade

  // Separar itens por categoria
  // SERVICO sai das "linhas móveis": licença de software não é linha de telefone.
  // Antes entrava aqui e a proposta que o cliente recebe mostrava "(número não
  // informado)" e "0GB" para um Microsoft 365, além de contar como linha no total.
  const linhasMoveis = temComposicao
    ? composicaoNova.linhas.filter(l => l.tipo !== 'FIBRA' && l.tipo !== 'TV' && l.tipo !== 'SERVICO')
    : [];
  const linhasServico = temComposicao
    ? composicaoNova.linhas.filter(l => l.tipo === 'SERVICO')
    : [];
  const linhaFibra = temComposicao 
    ? composicaoNova.linhas.find(l => l.tipo === 'FIBRA') 
    : undefined;
  const linhaTV = temComposicao 
    ? composicaoNova.linhas.find(l => l.tipo === 'TV') 
    : undefined;
  
  // Megabônus é ÚNICO (o número é do banco), não soma por linha
  const megabonus = composicaoNova ? megabonusDaProposta(composicaoNova) : 0;
  const beneficiosDosProdutos = useBeneficiosDaComposicao(composicaoNova);

  // Calcular total de pontos adicionais da TV
  const totalPontosTV = linhaTV?.adicionais 
    ? Object.values(linhaTV.adicionais).reduce((a, b) => a + b, 0)
    : 0;

  // Calcular adicionais de FIBRA
  const adicionaisFibra = linhaFibra?.adicionais || {};
  const qtdClaroFixo = adicionaisFibra['fibra-claro-fixo'] || 0;
  const qtdMesh = (adicionaisFibra['fibra-mesh-2'] || 0) * 2 + (adicionaisFibra['fibra-mesh-adicional'] || 0);

  // Formatar TV do plano novo
  const tvNovaFormatada = linhaTV 
    ? `${linhaTV.plano?.nome || 'Claro TV'}${totalPontosTV > 0 ? ` + ${totalPontosTV}pt(s)` : ''}`
    : '-';
  
  // Formatar Fibra com adicionais
  const fibraFormatada = linhaFibra 
    ? `${linhaFibra.plano?.velocidade ?? linhaFibra.plano?.nome ?? ''}${qtdClaroFixo > 0 ? ` +${qtdClaroFixo}Fixo` : ''}${qtdMesh > 0 ? ` +${qtdMesh}Mesh` : ''}`
    : '-';

  const gerarImagem = async () => {
    if (!cardRef.current) {
      toast.error('Não gerou a imagem do comparativo. Tente de novo.');
      return;
    }
    const toastId = toast.loading('Gerando imagem...');
    try {
      const dataUrl = await renderizarCardComoPng();
      const link = document.createElement('a');
      link.download = `proposta-claro-${nomeCliente?.replace(/\s+/g, '-') || 'cliente'}.png`;
      link.href = dataUrl;
      link.click();
      toast.dismiss(toastId);
      toast.success('Imagem gerada com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar imagem:', error);
      toast.dismiss(toastId);
      toast.error('Erro ao gerar imagem. Tente novamente.');
    }
  };

  const copiarProposta = () => {
    if (!temComposicao) {
      toast.error('Monte a proposta primeiro');
      return;
    }
    
    const texto = gerarMensagemPropostaCompleta({
      beneficiosDosProdutos,
      nomeCliente,
      razaoSocial: nomeCliente,
      origemLabel: origemLabel as 'PF' | 'CNPJ' | 'VIVO',
      planoAtual: {
        linhas: planoAtual.linhas,
        qtdLinhas: planoAtual.qtdLinhas ?? planoAtual.linhas.length,
        gb: planoAtual.gb,
        fibra: planoAtual.fibra,
        tv: planoAtual.tv,
        valor: planoAtual.valor,
      },
      composicaoNova: composicaoNova!,
      consultorNome,
      ocultarPlanoAtual, // Passar flag para omitir seção do plano atual
    });

    navigator.clipboard.writeText(texto);
    toast.success('Proposta copiada!');
  };

  // ══════════════════════════════════════════════════════════════════════
  // 67 §5.3 — A ARTE aprovada: hero vinho (Empresarial Prime · Proposta de
  // {tipo} + razão/CNPJ + data), lista de linhas com valores, "+ N linhas ·
  // X GB no total", bloco DE (riscado) → POR · ECONOMIA, chips de benefícios
  // e rodapé "Consultor {nome} · emitida em {data}". Uma peça só, derivada
  // do resumo — idêntica em todo tipo de lead.
  // ══════════════════════════════════════════════════════════════════════
  const dataHoje = new Date().toLocaleDateString('pt-BR');
  const tiposComposicao = temComposicao ? [...new Set(composicaoNova!.linhas.map(l => l.tipo))] : [];
  const soDe = (...ts: TipoLinha[]) => tiposComposicao.length > 0 && tiposComposicao.every(t => ts.includes(t));
  const ehCpf = !!composicaoNova && (composicaoNova.tipoPessoa === 'PF' || composicaoNova.linhas.some(l => l.plano?.tipoPessoa === 'PF'));
  const docDigitos = String(cnpj || '').replace(/\D/g, '');
  const documentoFormatado = ehCpf || docDigitos.length === 11
    ? docDigitos.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
    : (cnpj ? formatarCNPJ(cnpj) : '');
  const tituloProposta = soDe('RENOVACAO')
    ? 'Proposta de Renovação'
    : soDe('PORTABILIDADE', 'LINHA_NOVA', 'NOVA')
      ? 'Proposta de Portabilidade'
      : soDe('TT', 'TT_PRE')
        ? (ehCpf ? 'Proposta de Transferência de Titularidade' : 'Proposta de Migração para CNPJ')
        : soDe('FIBRA', 'TV')
          ? 'Proposta Claro Fibra & TV'
          : (ehCpf ? 'Proposta Claro' : 'Proposta Comercial');

  const valorDaLinha = (l: ComposicaoProposta['linhas'][number]) => {
    const receita = Number((l as { receitaRenovada?: number }).receitaRenovada || 0);
    const base = l.tipo === 'RENOVACAO' && receita > 0 ? receita : Number(l.plano?.valor || 0);
    const adicionais = l.adicionais
      ? Object.entries(l.adicionais).reduce((soma, [adicId, qtd]) => {
          const adicTV = ADICIONAIS_TV.find(a => a.id === adicId);
          const adicFibra = ADICIONAIS_FIBRA.find(a => a.id === adicId);
          return soma + ((adicTV?.valor || adicFibra?.valor || 0) * (qtd || 0));
        }, 0)
      : 0;
    return base * (l.tipo === 'SERVICO' ? Math.max(1, l.quantidade ?? 1) : 1) + adicionais;
  };

  const descricaoDaLinha = (l: ComposicaoProposta['linhas'][number]) => {
    const base = descricaoBaseDaLinha(l);
    const meses = Number(l.plano?.mesesPromocionais || 0);
    const depois = Number(l.plano?.valorAposPromocao || 0);
    return ehCpf && meses && depois ? `${base} · ${meses} meses, depois ${formatarMoedaPlano(depois)}` : base;
  };
  const descricaoBaseDaLinha = (l: ComposicaoProposta['linhas'][number]) => {
    const gb = l.plano?.gb ? `${l.plano.gb}GB` : '';
    switch (l.tipo) {
      case 'RENOVACAO': return 'Plano renovado';
      case 'TT': case 'TT_PRE': { const t = ehCpf ? 'Troca de titular' : 'Migra p/ CNPJ'; return gb ? `${t} · ${gb}` : t; }
      case 'PORTABILIDADE': return gb ? `Portabilidade · ${gb}` : 'Portabilidade';
      case 'LINHA_NOVA': return gb ? `Linha nova · ${gb}` : 'Linha nova';
      case 'NOVA': return gb ? `Bloco de dados · ${gb}` : 'Bloco de dados';
      case 'FIBRA': return fibraFormatada;
      case 'TV': return tvNovaFormatada;
      case 'SERVICO': return `${Math.max(1, l.quantidade ?? 1)}× licença`;
      default: return '';
    }
  };

  const rotuloDaLinha = (l: ComposicaoProposta['linhas'][number]) => {
    if (l.tipo === 'FIBRA') return 'Claro Fibra';
    if (l.tipo === 'TV') return l.plano?.nome || 'Claro TV';
    if (l.tipo === 'SERVICO') return l.plano?.nome || 'Serviço digital';
    if (l.tipo === 'LINHA_NOVA') {
      const ddd = String(l.numero || '').replace(/\D/g, '');
      return ddd.length === 2 ? `Linha nova (DDD ${ddd})` : 'Linha nova';
    }
    if (l.tipo === 'NOVA') return 'Bloco de dados';
    const digits = String(l.numero || '').replace(/\D/g, '');
    return digits.length >= 10 ? formatarTelefone(l.numero!) : '(número não informado)';
  };

  const nomeComercial = (nome: string) => nome
    .toLocaleLowerCase('pt-BR')
    .replace(/\b\p{L}/gu, (letra) => letra.toLocaleUpperCase('pt-BR'))
    .replace(/\bIphone\b/g, 'iPhone')
    .replace(/\bGb\b/g, 'GB')
    .replace(/\b5g\b/gi, '5G')
    .replace(/\bOnline\b/g, 'Online');

  const LIMITE_LINHAS = 8;
  const todasLinhasArte = temComposicao ? composicaoNova!.linhas : [];
  const linhasArte = todasLinhasArte.filter((l) => l.tipo !== 'SERVICO');
  const acompanhamentosDaLinha = (linha: ComposicaoProposta['linhas'][number]) =>
    todasLinhasArte.filter((item) => item.tipo === 'SERVICO' && item.linhaVinculadaId === linha.linha_id);
  // ⛔ Mesmo buraco que o Douglas ja' tinha pego na mensagem de texto em
  // 21/08 ("o aparelho entrava no pedido mas nao aparecia em lugar nenhum
  // do que o cliente le"): o conserto (listarAparelhos) so' foi ligado em
  // gerarMensagemPropostaCompleta — a ARTE (PNG) nunca chamou a mesma
  // funcao e continuava sem mostrar o aparelho vendido. (Douglas 04/09/2026:
  // "veja se colocar produtos com aparelho... ele entra na proposta?")
  const aparelhosArte = temComposicao ? listarAparelhos(composicaoNova!.linhas) : [];
  const linhasVisiveis = linhasArte.slice(0, LIMITE_LINHAS);
  const linhasRestantes = Math.max(0, linhasArte.length - LIMITE_LINHAS);
  const totalGBArte = temComposicao ? Number(composicaoNova!.totalGB || 0) : 0;
  const mostrarDe = !ocultarPlanoAtual && planoAtual.valor > 0;
  const mostrarEconomia = mostrarDe && economia > 0;

  return (
    <div className="min-w-0">
      {/* ações fora da arte */}
      <div className="mb-2 flex items-center justify-between gap-2 print:hidden">
        <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-foreground">Proposta · Imagem</p>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[11px]"
            onClick={copiarProposta}
          >
            <Copy className="mr-1 h-3 w-3" />
            Copiar
          </Button>
          <Button
            size="sm"
            className="h-7 border-0 bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] text-[11px] font-bold text-white hover:opacity-90 dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]"
            onClick={gerarImagem}
          >
            <Download className="mr-1 h-3 w-3" />
            Baixar PNG
          </Button>
        </div>
      </div>

      {/* A ARTE — é exatamente isto que vira PNG e vai no WhatsApp */}
      <div ref={cardRef} className="overflow-hidden rounded-xl border border-[#E7DCCB] bg-white text-[#2E2019]">
        <div className="bg-[linear-gradient(135deg,#4A1320_0%,#5B0A0D_55%,#6E1626_100%)] px-4 py-3 text-white">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold text-white/85">{ehCpf ? <><span className="font-bold text-white">Prime</span> Conecta</> : <>Empresarial <span className="font-bold text-white">Prime</span></>}</p>
              <h3 className="font-display text-[17px] font-bold leading-tight">{tituloProposta}</h3>
              {(nomeCliente || cnpj) && (
                <p className="mt-0.5 truncate text-[10.5px] text-white/75">
                  {nomeCliente || ''}{nomeCliente && cnpj ? ' · ' : ''}{documentoFormatado}
                </p>
              )}
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              <span className="text-[10.5px] tabular-nums text-white/75">{dataHoje}</span>
              {consultorFotoUrl && (
                <Avatar className="h-9 w-9 border-2 border-white/30">
                  <AvatarImage src={consultorFotoUrl} alt={consultorNome || 'Consultor'} />
                  <AvatarFallback className="bg-brand-vinho text-xs font-bold text-white">
                    {consultorNome ? consultorNome.split(' ').filter(pp => pp.length > 0).map(pp => pp[0]).slice(0, 2).join('').toUpperCase() : '?'}
                  </AvatarFallback>
                </Avatar>
              )}
            </div>
          </div>
        </div>

        {temComposicao ? (
          <div className="px-4 py-2">
            {linhasVisiveis.map((l, i) => (
              <div key={l.id || i} className="border-b border-[#F0E9E1] py-1.5 last:border-b-0">
                <div className="flex items-center gap-2">
                  <span className="flex-shrink-0 text-xs font-semibold tabular-nums">{rotuloDaLinha(l)}</span>
                  <span className="min-w-0 flex-1 truncate text-[10.5px] text-[#8A7576]">{descricaoDaLinha(l)}</span>
                  <span className="flex-shrink-0 text-xs font-semibold tabular-nums">{formatarMoedaPlano(valorDaLinha(l))}</span>
                </div>
                {acompanhamentosDaLinha(l).map((item) => (
                  <div key={item.id} className="mt-1 flex items-center gap-2 pl-3 text-[10px] text-[#8A7576]">
                    <span>{item.categoriaCustom === 'aparelhos' ? '📱' : '↳'}</span>
                    <span className="min-w-0 flex-1 truncate">{nomeComercial(item.plano?.nome || 'Serviço digital')}</span>
                    <span>{item.categoriaCustom === 'aparelhos' ? '1 unidade' : `${Math.max(1, item.quantidade ?? 1)} licença`}</span>
                  </div>
                ))}
              </div>
            ))}
            <p className="py-1.5 text-[10px] text-[#8A7576]">
              {linhasRestantes > 0 ? `+ ${linhasRestantes} ${linhasRestantes === 1 ? 'linha' : 'linhas'} · ` : ''}
              {linhasArte.length} {linhasArte.length === 1 ? 'linha' : 'linhas'} na proposta · {todasLinhasArte.length - linhasArte.length} acompanhamento(s){totalGBArte > 0 ? ` · ${totalGBArte} GB no total` : ''}
            </p>

            {/* DE (riscado) → POR · ECONOMIA */}
            <div className={`mb-3 mt-1 grid divide-x divide-[#F0E9E1] rounded-lg border border-[#E7DCCB] ${mostrarDe ? 'grid-cols-3' : 'grid-cols-1'}`}>
              {mostrarDe && (
                <div className="px-3 py-2">
                  <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#8A7576]">De</p>
                  <p className="font-display text-[15px] font-bold tabular-nums text-[#8A7576] line-through">{formatarMoedaPlano(planoAtual.valor)}</p>
                </div>
              )}
              <div className="px-3 py-2">
                <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#8A7576]">Por</p>
                <p className="font-display text-[19px] font-bold leading-tight tabular-nums text-[#6E1626]">{formatarMoedaPlano(totalValorNovo)}<span className="text-[10px] font-semibold text-[#8A7576]">/mês</span></p>
              </div>
              {mostrarDe && (
                <div className="px-3 py-2">
                  <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#8A7576]">Economia</p>
                  {mostrarEconomia ? (
                    <p className="font-display text-[15px] font-bold tabular-nums text-[#075E54]">{formatarMoedaPlano(economia)}<span className="text-[10px] font-semibold text-[#8A7576]">/mês</span></p>
                  ) : (
                    <p className="font-display text-[15px] font-bold text-[#B4808C]">—</p>
                  )}
                </div>
              )}
            </div>

            {/* benefícios */}
            <div className="flex flex-wrap gap-1.5 pb-2">
              {beneficiosDosProdutos.map(b => (
                <span key={b} className="inline-flex items-center gap-1 rounded-full border border-[#E7DCCB] bg-white px-2 py-0.5 text-[10px]">
                  <Check className="h-2.5 w-2.5 text-[#075E54]" />
                  {b}
                </span>
              ))}
              {megabonus > 0 && (
                <span className="inline-flex items-center rounded-full border border-[#E7DCCB] bg-white px-2 py-0.5 text-[10px]">
                  <span className="font-bold text-[#075E54]">+{megabonus}GB</span>&nbsp;Megabônus
                </span>
              )}
            </div>

            {/* Aparelho — nao entra no total mensal (e' parcelado na fatura,
                separado do faturamento por regra); so' informa o que o
                cliente vai ver cobrado e em qual linha. Mesma frase da
                mensagem de texto (listarAparelhos), pra bater com o que o
                consultor ja' manda pelo WhatsApp. */}
            {aparelhosArte.length > 0 && (
              <div className="mb-1 space-y-1 border-t border-[#F0E9E1] pt-2">
                <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#8A7576]">
                  {aparelhosArte.length === 1 ? 'Aparelho' : `${aparelhosArte.length} aparelhos`}
                </p>
                {aparelhosArte.map((ap, i) => (
                  <p key={i} className="text-[10.5px] text-[#2E2019]">
                    📱 <span className="font-semibold">{nomeComercial(ap.nome)}</span> — {ap.forma}{ap.linha}
                  </p>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center px-4 py-10">
            <div className="rounded-lg border border-dashed border-[#E7DCCB] p-6 text-center">
              <Phone className="mx-auto mb-2 h-6 w-6 text-[#B4808C]" />
              <p className="text-sm text-[#8A7576]">Monte a proposta acima para gerar a arte</p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-[#F0E9E1] bg-[#FBFAF9] px-4 py-2 text-[10px] text-[#8A7576]">
          <span className="min-w-0 truncate">Consultor <strong className="font-bold text-[#2E2019]">{consultorNome || '—'}</strong> · {ehCpf ? 'Prime Conecta' : 'Empresarial Prime'}</span>
          <span className="flex-shrink-0 tabular-nums">emitida em {dataHoje}</span>
        </div>
      </div>
      <p className="mt-1 text-[10px] text-muted-foreground print:hidden">gerada dos valores do resumo — é a arte que vai no tipo Proposta Imagem</p>
    </div>
  );
});
